import type React from 'react';
import type { Id } from '../../convex/_generated/dataModel';

interface TagChipProps {
  tag: {
    _id: Id<'tags'>;
    name: string;
  };
  onClick?: (tagId: Id<'tags'>) => void;
  variant?: 'default' | 'filter' | 'negative' | 'active';
  size?: 'sm' | 'md';
  className?: string;
  showClickHint?: boolean;
  isActive?: boolean;
}

export function TagChip({ 
  tag, 
  onClick, 
  variant = 'default', 
  size = 'sm',
  className = '',
  showClickHint = false,
  isActive = false
}: TagChipProps) {
  const isClickable = !!onClick;
  
  // Auto-select variant based on isActive if not explicitly set
  const effectiveVariant = isActive && variant === 'default' ? 'active' : variant;
  
  const baseClasses = `
    inline-flex items-center gap-1 font-medium transition-all duration-200
    ${isClickable ? 'cursor-pointer' : 'cursor-default'}
    ${isClickable ? 'hover:scale-105 active:scale-95' : ''}
  `;
  
  const sizeClasses = {
    sm: 'badge-sm px-2 py-1',
    md: 'badge-md px-2 py-1'
  };
  
  const variantClasses = {
    default: isClickable 
      ? 'badge badge-outline hover:badge-primary focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1'
      : 'badge badge-outline',
    filter: isClickable
      ? 'badge badge-success hover:bg-success hover:text-success-content focus-visible:ring-2 focus-visible:ring-success focus-visible:ring-offset-1'
      : 'badge badge-success',
    negative: isClickable
      ? 'badge badge-error hover:bg-error hover:text-error-content focus-visible:ring-2 focus-visible:ring-error focus-visible:ring-offset-1'
      : 'badge badge-error',
    active: isClickable
      ? 'badge badge-primary hover:bg-primary-focus focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1'
      : 'badge badge-primary'
  };
  
  const focusClasses = isClickable 
    ? 'focus:outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1'
    : '';
  
  const combinedClasses = `
    ${baseClasses}
    ${sizeClasses[size]}
    ${variantClasses[effectiveVariant]}
    ${focusClasses}
    ${className}
  `.trim().replace(/\s+/g, ' ');

  const handleClick = () => {
    if (onClick) {
      onClick(tag._id);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      handleClick();
    }
  };

  const ariaLabel = isClickable 
    ? `Filter by tag: ${tag.name}${showClickHint ? ' (click to filter)' : ''}`
    : `Tag: ${tag.name}`;

  if (isClickable) {
    return (
      <button
        type="button"
        className={combinedClasses}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        aria-label={ariaLabel}
        title={showClickHint ? `Click to filter by "${tag.name}"` : undefined}
      >
        {tag.name}
      </button>
    );
  }

  return (
    <span
      className={combinedClasses}
      aria-label={ariaLabel}
    >
      {tag.name}
    </span>
  );
} 